import {
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import {
  STATIC_DATA,
  STATIC_JSON_DATA_PATH,
} from '../../../../shared/constants/static-data.constants';
import { ListTableComponent } from '../../../../shared/components/list-table/list-table.component';
import { Router } from '@angular/router';
import { SectionsService } from '../../../../services/sections.service';
import { SharedDataService } from '../../../../shared/services/shared-data-service';
import { BillingService } from '../../../../services/billing.service';
import {
  DialogConfig,
  DialogService,
} from '../../../../shared/services/dialog.service';
import { BillingFilterComponent } from '../../billing-filter/billing-filter.component';
import { ListService } from '../../../../shared/services/list.service';
import { IgnoredTimesheetsComponent } from '../../ignored-timesheets/ignored-timesheets/ignored-timesheets.component';
import { UserService } from '../../../../services/user.service';
import { LoggerService } from '../../../../shared/services/logger.service';

@Component({
  selector: 'ppl-batches-list',
  standalone: true,
  imports: [
    ListTableComponent,
    IgnoredTimesheetsComponent,
    BillingFilterComponent,
  ],
  templateUrl: './batches-list.component.html',
  styleUrl: './batches-list.component.css',
})
export class BatchesListComponent implements OnInit {
  sharedDataKey: string = STATIC_DATA.SHARED_BATCHES_DATA;
  pageId: string = PAGE_CONSTANTS.BATCHES_GRID_PAGE;
  programName!: string;
  lazyLoad = true;
  ignoredTimesheetData: any;
  ignoredTimesheetSummaryData: any;
  appliedFilters: any;
  batches: any[] = [];
  selectedBatchId: any;

  @ViewChild('timesheetDialog') timesheetDialog!: TemplateRef<any>;
  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;

  private dialogSvc = inject(DialogService);

  cols = [];
  fields: any[] = [
    { field: 'timeframe', show: true },
    { field: 'mcoName', show: true },
    { field: 'batchName', show: true },
    { field: 'pmpm', show: true },
    { field: 'runId', show: true },
    { field: 'batchDate', show: true },
  ];

  constructor(
    private router: Router,
    private sharedDataService: SharedDataService,
    private sectionService: SectionsService,
    private billingService: BillingService,
    private listService: ListService,
    private userService: UserService,
    private logger: LoggerService
  ) {}

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    // this.setPageAccess();
    this.getInitData();
    this.getColumnData();
    const navigationState = window.history.state;
    const filters = [];
    this.fields = this.fields.map((field) => {
      if (field.field === 'mcoName' && navigationState.mco) {
        return { ...field, default: navigationState.mco };
      }
      if (field.field === 'pmpm' && navigationState.pmpm) {
        return {
          ...field,
          default: navigationState.pmpm === 'PMPM' ? 'Yes' : 'No',
        };
      }
      return field;
    });

    if (navigationState.mco) {
      filters.push({
        jsonPath: 'mcoName',
        value: navigationState.mco,
        operator: 'Equal',
      });
    }

    if (navigationState.pmpm) {
      filters.push({
        jsonPath: 'pmpmFlg',
        value: navigationState.pmpm === 'PMPM' ? 'Yes' : 'No',
        operator: 'Equal',
      });
    }

    //this.getInitData();
    this.searchBatches(filters);
  }

  async getColumnData() {
    try {
      const tempCols = await this.sectionService.getJsonConfigurationData(
        STATIC_JSON_DATA_PATH.BATCHES_LIST_PAGE_HEADERS,
        'billing'
      );
      this.cols = tempCols.columns;
    } catch (e) {
      this.logger.debug('Error in getting column data', e);
    }
  }

  getInitData() {
    this.searchBatches([]);
  }

  rowClicked(rowDetail: any) {
    this.userService.setLeadEntry('batches');
    if (rowDetail.pmpmFlg === 'No') {
      this.router.navigate(['/home/viewClaimsList'], {
        state: {
          batchId: rowDetail.tasBillingBatchMasterId,
          pmpmflg: rowDetail.pmpmFlg,
        },
      });
    } else {
      this.router.navigate(['/home/billingPmpmClaimsList'], {
        state: {
          batchId: rowDetail.tasBillingBatchMasterId,
          pmpmflg: rowDetail.pmpmFlg,
        },
      });
    }
  }

  onPageLoad(event: any) {
    this.searchBatches(
      this.appliedFilters,
      event.startingPageNumber,
      event?.first ?? 0
    );
  }

  onSearch(filters: any) {
    this.appliedFilters = filters;
    this.searchBatches(filters);
  }

  buildRequestPayload(
    pageName: string,
    orderBy: string,
    searchParams: [],
    startingPageNumber = 1
  ) {
    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage: 20,
      startingPageNumber: startingPageNumber,
      searchParam: searchParams,
      orderByColumn: orderBy ? [orderBy] : [],
      orderBy: ['desc'],
    };
    return payload;
  }
  searchBatches(filters: any, startingPageNumber = 1, currentPage = 0) {
    if (startingPageNumber == 1) {
      this.batches = [];
    }
    const processedPayload = this.buildRequestPayload(
      'billing-batch-master-list',
      'createddate',
      filters,
      startingPageNumber
    );
    this.listService
      .getListData(processedPayload, 'billing')
      .subscribe((data: any) => {
        const records = data.responsedata;
        if (startingPageNumber > 1) {
          this.batches = [...this.batches, ...records];
        } else {
          this.batches = records;
        }
        this.batches = this.formatAmounts(this.batches);
        if (this.childComponent) {
          this.childComponent.resetPagination(startingPageNumber, currentPage);
        }
      });
  }

  formatAmounts(batches: any) {
    return batches.map((batch: any) => {
      return {
        ...batch,
        totalCharges: batch.totalCharges
          ? this.billingService.formatCurrency(batch.totalCharges)
          : batch.totalCharges,
      };
    });
  }

  clearData() {
    this.sharedDataService.clearData(this.sharedDataKey);
  }

  openTimesheetDialog() {
    const dialogConfig: DialogConfig = {
      title: `Ignored Timesheets`,
      isOkText: 'Deny',
      size: 'x-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.timesheetDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  handleCancel() {}

  rowClickedTs(event: any) {
    if (event?.col?.field === 'ignoredTimesheetCount') {
      this.selectedBatchId = event.event.tasBillingBatchMasterId;
      this.searchIgnoredTimesheetsData(event.event.tasBillingBatchMasterId);
      this.getIgnoredTimesheetsSummaryData(event.event.tasBillingBatchMasterId);
    }
  }

  getIgnoredTimesheetsData(id: any) {
    this.billingService
      .getIgnoredTimesheets(this.programName, id)
      .subscribe((data: any) => {
        this.ignoredTimesheetData = data.responsedata;
      });
  }

  searchIgnoredTimesheetsData(id: any) {
    const payload = {
      timesheetId: '',
      providerPplId: '',
      participantPplId: '',
      programCode: this.programName,
      billingBatchMaster: id,
    };
    this.billingService
      .searchIgnoredTimesheets(payload)
      .subscribe((data: any) => {
        this.ignoredTimesheetData = data.responsedata;
      });
  }

  getIgnoredTimesheetsSummaryData(id: any) {
    this.billingService
      .getIgnoredTimesheetsSummary(this.programName, id)
      .subscribe((data: any) => {
        this.ignoredTimesheetSummaryData = data.responsedata;
        if (this.ignoredTimesheetSummaryData) {
          this.openTimesheetDialog();
        }
      });
  }
}
